import React from 'react';
import EmojiEventsRoundedIcon from '@mui/icons-material/EmojiEventsRounded';
import LeaderboardRoundedIcon from '@mui/icons-material/LeaderboardRounded';
import {
  Avatar,
  Box,
  Chip,
  LinearProgress,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { formatDate } from '../utils/format';
import WidgetShell from './widgets/WidgetShell';

const getRankMeta = (index) => {
  if (index === 0) {
    return { label: 'Gold', tone: '#b8870f' };
  }

  if (index === 1) {
    return { label: 'Silver', tone: '#8a96a8' };
  }

  if (index === 2) {
    return { label: 'Bronze', tone: '#a15d2f' };
  }

  return null;
};

function TopWordsWidget({ topWordPlays = [] }) {
  const theme = useTheme();
  const topWordMaxScore = topWordPlays.length
    ? Math.max(...topWordPlays.map((entry) => Number(entry.score || 0)), 1)
    : 1;

  return (
    <WidgetShell
      icon={<LeaderboardRoundedIcon color="primary" fontSize="small" />}
      title="Your Top Words"
      subtitle="Your best lifetime word scores and when they happened."
      action={
        <Chip
          size="small"
          label="Lifetime Top 5"
          sx={{ fontWeight: 700, borderRadius: 1.5 }}
          color="primary"
          variant="outlined"
        />
      }
    >
      {topWordPlays.length ? (
        <List disablePadding>
          {topWordPlays.map((entry, index) => {
            const rankMeta = getRankMeta(index);
            const rankTone = rankMeta?.tone || theme.palette.primary.main;

            return (
              <ListItem
                key={`${entry.word || 'word'}-${entry.timestamp || index}-${index}`}
                disableGutters
                sx={{
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.16)}`,
                  borderRadius: 2,
                  px: 1.1,
                  py: 1,
                  mb: index === topWordPlays.length - 1 ? 0 : 0.9,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.background.paper, 0.9)} 70%)`,
                  transition: 'transform 140ms ease, box-shadow 140ms ease',
                  '&:hover': {
                    transform: 'translateY(-1px)',
                    boxShadow: `0 8px 20px ${alpha(theme.palette.primary.main, 0.15)}`,
                  },
                }}
              >
                <ListItemAvatar sx={{ minWidth: 44 }}>
                  <Avatar
                    sx={{
                      width: 30,
                      height: 30,
                      fontSize: 13,
                      fontWeight: 800,
                      bgcolor: alpha(rankTone, 0.18),
                      color: rankTone,
                    }}
                  >
                    {index + 1}
                  </Avatar>
                </ListItemAvatar>

                <ListItemText
                  secondaryTypographyProps={{ component: 'div' }}
                  primary={
                    <Stack direction="row" spacing={0.8} alignItems="center" flexWrap="wrap">
                      <Typography sx={{ fontWeight: 800, letterSpacing: 0.2 }}>
                        {String(entry.word || '--').toUpperCase()}
                      </Typography>
                      {rankMeta ? (
                        <Chip
                          size="small"
                          label={rankMeta.label}
                          icon={index === 0 ? <EmojiEventsRoundedIcon sx={{ fontSize: 14 }} /> : undefined}
                          sx={{
                            height: 21,
                            fontWeight: 700,
                            color: rankTone,
                            backgroundColor: alpha(rankTone, 0.14),
                          }}
                        />
                      ) : null}
                    </Stack>
                  }
                  secondary={
                    <Stack spacing={0.55} sx={{ mt: 0.45 }}>
                      <Typography variant="caption" color="text.secondary">
                        R{Number(entry.roundIndex || 1)} • Submit {Number(entry.submitScore || 0)} pts • {Number(entry.submitWordCount || 0)} words • {formatDate(entry.timestamp)}
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={Math.max(2, Math.min(100, (Number(entry.score || 0) / topWordMaxScore) * 100))}
                        sx={{
                          height: 6,
                          borderRadius: 999,
                          backgroundColor: alpha(theme.palette.primary.main, 0.12),
                          '& .MuiLinearProgress-bar': {
                            borderRadius: 999,
                          },
                        }}
                      />
                    </Stack>
                  }
                />

                <Stack alignItems="flex-end" sx={{ ml: 1 }}>
                  <Chip
                    size="small"
                    label={`${Number(entry.score || 0)} pts`}
                    sx={{
                      fontWeight: 800,
                      height: 24,
                      color: rankTone,
                      backgroundColor: alpha(rankTone, 0.16),
                    }}
                  />
                </Stack>
              </ListItem>
            );
          })}
        </List>
      ) : (
        <Box
          sx={{
            border: `1px dashed ${alpha(theme.palette.primary.main, 0.24)}`,
            borderRadius: 2,
            px: 1.6,
            py: 2,
            backgroundColor: alpha(theme.palette.primary.main, 0.03),
          }}
        >
          <Typography color="text.secondary">
            No lifetime top words yet. Start a game and submit words to build your leaderboard.
          </Typography>
        </Box>
      )}
    </WidgetShell>
  );
}

export default TopWordsWidget;
